<template>
  <div class="max-w-6xl mx-auto">
    <div class="bg-background-light rounded-lg shadow-sm">
      <div class="p-6 border-b border-background-lighter">
        <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 class="text-2xl font-bold text-text-primary">Transaction History</h2>
            <p class="text-text-secondary mt-1">View all your financial transactions</p>
          </div>

          <!-- Filters -->
          <div class="mt-4 sm:mt-0 flex space-x-3">
            <select
                v-model="filters.type"
                @change="fetchTransactions"
                class="px-3 py-2 border border-background-lighter rounded-md bg-background focus:outline-none focus:border-primary"
            >
              <option value="">All Types</option>
              <option value="Purchase">Purchase</option>
              <option value="Deposit">Deposit</option>
              <option value="Withdrawal">Withdrawal</option>
              <option value="Refund">Refund</option>
            </select>

            <input
                v-model="filters.date_from"
                type="date"
                class="px-3 py-2 border border-background-lighter rounded-md bg-background focus:outline-none focus:border-primary"
                @change="fetchTransactions"
            />

            <input
                v-model="filters.date_to"
                type="date"
                class="px-3 py-2 border border-background-lighter rounded-md bg-background focus:outline-none focus:border-primary"
                @change="fetchTransactions"
            />
          </div>
        </div>
      </div>

      <div class="p-6">
        <!-- Loading State -->
        <div v-if="isLoading" class="flex justify-center items-center py-8">
          <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>

        <!-- Transactions Table -->
        <div v-else-if="transactions.length > 0" class="overflow-x-auto">
          <table class="w-full">
            <thead class="bg-background-lighter">
            <tr>
              <th class="px-4 py-3 text-left text-sm font-medium text-text-secondary">Date</th>
              <th class="px-4 py-3 text-left text-sm font-medium text-text-secondary">Description</th>
              <th class="px-4 py-3 text-left text-sm font-medium text-text-secondary">Amount</th>
              <th class="px-4 py-3 text-left text-sm font-medium text-text-secondary">New Balance</th>
            </tr>
            </thead>
            <tbody class="divide-y divide-background-lighter">
            <tr
                v-for="transaction in transactions"
                :key="transaction.id"
                class="hover:bg-background-lighter"
            >
              <td class="px-4 py-3 text-sm text-text-primary">
                {{ formatDate(transaction.created_at) }}
              </td>

              <td class="px-4 py-3 text-sm text-text-primary">
                <span v-html="transaction.desc || ''"></span>

              </td>

              <td class="px-4 py-3 text-sm font-medium">
                <span :class="transaction.amount >= 0 ? 'text-green-600' : 'text-red-600'">
                  {{ transaction.amount >= 0 ? '+' : '-' }}{{  transaction.amount }}
                </span>
              </td>

              <td class="px-4 py-3 text-sm text-text-primary">
                {{ extractBalance(transaction.new_wallet) }}
              </td>
            </tr>
            </tbody>
          </table>
        </div>

        <!-- Empty State -->
        <div v-else class="text-center py-8">
          <svg class="mx-auto h-12 w-12 text-text-secondary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z"></path>
          </svg>
          <p class="mt-2 text-text-secondary">No transactions found</p>
        </div>

        <!-- Pagination -->
        <div v-if="pagination.total > pagination.per_page" class="mt-6 flex justify-center">
          <nav class="flex items-center space-x-2">
            <button
                @click="changePage(pagination.current_page - 1)"
                :disabled="pagination.current_page === 1"
                class="px-3 py-2 text-sm bg-background border border-background-lighter rounded hover:bg-background-lighter disabled:opacity-50"
            >
              Previous
            </button>

            <span class="px-3 py-2 text-sm text-text-secondary">
              Page {{ pagination.current_page }} of {{ pagination.last_page }}
            </span>

            <button
                @click="changePage(pagination.current_page + 1)"
                :disabled="pagination.current_page === pagination.last_page"
                class="px-3 py-2 text-sm bg-background border border-background-lighter rounded hover:bg-background-lighter disabled:opacity-50"
            >
              Next
            </button>
          </nav>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted } from 'vue';
import { useDataStore } from '@/stores/dataStore.js';
import { getUserTransactions } from '@/services/api.js';
import Swal from 'sweetalert2';

export default {
  name: 'TransactionHistory',
  setup() {
    const dataStore = useDataStore();

    const transactions = ref([]);
    const isLoading = ref(true);

    const filters = ref({
      type: '',
      date_from: '',
      date_to: ''
    });

    const pagination = ref({
      current_page: 1,
      last_page: 1,
      per_page: 10,
      total: 0
    });

    const formatCurrency = (amount) => {
      const symbol = dataStore.currencySymbol || 'Dzd';
      return `${amount} ${symbol}`;
    };

    const formatDate = (dateString) => {
      if (!dateString) return 'N/A';
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    };

    const extractBalance = (newWalletString) => {
      if (!newWalletString) return 'N/A';
      // Match "New wallet sum is: -528.00 Dzd", "$", "€", or "EGP"
      const match = newWalletString.match(/:\s*([-\d.,]+)\s*(Dzd|\$|€|EGP)/i);
      return match ? `${match[1]} ${match[2]}` : 'N/A';
    };


    const fetchTransactions = async (page = 1) => {
      isLoading.value = true;

      try {
        const params = {
          page,
          per_page: pagination.value.per_page,
          type: filters.value.type,
          date_from: filters.value.date_from,
          date_to: filters.value.date_to
        };

        const response = await getUserTransactions(params);

        const data = response.data || response;
        transactions.value = data.transactionsHistories || [];
        pagination.value = data.pagination || pagination.value;


      } catch (error) {
        console.error('Failed to fetch transactions:', error);
        Swal.fire({
          title: 'Error!',
          text: 'Failed to load transactions. Please try again.',
          icon: 'error'
        });
      } finally {
        isLoading.value = false;
      }
    };

    const changePage = (page) => {
      if (page >= 1 && page <= pagination.value.last_page) {
        fetchTransactions(page);
      }
    };

    onMounted(() => {
      fetchTransactions();
    });

    return {
      transactions,
      isLoading,
      filters,
      pagination,
      formatCurrency,
      formatDate,
      extractBalance,
      fetchTransactions,
      changePage
    };
  }
}
</script>
